'use client';

import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import Cookies from 'js-cookie';
import { useRouter } from 'next/navigation';

interface User {
  _id: string;
  name: string;
  email: string;
  role: 'admin' | 'superadmin';
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (token: string, userData: User) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const router = useRouter();

  useEffect(() => {
    // 1. Look for the exact keys we set during login
    const token = Cookies.get("adminToken");
    const savedUser = localStorage.getItem("adminUser"); // Using localStorage for the JSON object

    if (token && savedUser) {
      try {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setUser(JSON.parse(savedUser));
      } catch (error) {
        console.error("Failed to parse user data", error);
      }
    }
    setLoading(false);
  }, []);

  const login = (token: string, userData: User) => {
    // 2. Set token in Cookies (good for API/Middleware)
    Cookies.set("adminToken", token, { expires: 1 }); 
    
    // 3. Set User (Role/Name) in LocalStorage (best for JSON)
    localStorage.setItem("adminUser", JSON.stringify(userData)); 
    
    setUser(userData);
    router.push("/dashboard");
  };

  const logout = () => {
    // 4. Wipe React State
    setUser(null);
    
    // 5. Wipe Cookies and LocalStorage completely
    Cookies.remove('adminToken');
    Cookies.remove('adminUser'); // Just in case old cookie versions exist
    Cookies.remove('token');     // Just in case
    localStorage.removeItem('adminUser');
    localStorage.removeItem('user'); 
    
    // 6. Force a hard redirect to wipe any remaining browser memory
    window.location.href = '/login';
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};